import { isInIframe } from "@repo/ui/helpers/helpers";

const sessionItemKey = "ar-embedded";

const SessionItemValue = {
  Fountain: "fountain",
} as const;

// A third-party frame can have storage blocked outright (Firefox strict ETP,
// Safari lockdown), where touching sessionStorage throws instead of returning
// null. Detection has to degrade rather than take down the render.
function readEmbedSessionItem(): string | null {
  try {
    return window.sessionStorage.getItem(sessionItemKey);
  } catch {
    return null;
  }
}

function writeEmbedSessionItem(value: string): void {
  try {
    window.sessionStorage.setItem(sessionItemKey, value);
  } catch {
    // The in-memory result still covers the rest of this page load.
  }
}

let isEmbeddedInFountain: boolean | null = null;

/**
 * Resolves whether this page load is running inside Fountain and caches the
 * answer for the rest of the load. Fountain frames us with `?int=fountain`; the
 * session flag carries that across in-app navigations and reloads that drop the
 * param.
 *
 * Call once from the app entry point, before the first render — consumers read
 * the cached result during render and are never notified of a later change.
 */
export function detectIsEmbeddedInFountain(): boolean {
  const queryParams = new URLSearchParams(window.location.search);
  const isComingFromFountain = queryParams.get("int") === "fountain";

  if (isComingFromFountain && isInIframe()) {
    writeEmbedSessionItem(SessionItemValue.Fountain);
    isEmbeddedInFountain = true;
  } else {
    // No marker on this URL, so fall back to a flag set by an earlier load in
    // this session rather than overriding it.
    isEmbeddedInFountain = readEmbedSessionItem() === SessionItemValue.Fountain;
  }

  return isEmbeddedInFountain;
}

export function getIsEmbeddedInFountain(): boolean {
  return isEmbeddedInFountain ?? detectIsEmbeddedInFountain();
}

export function useIsEmbeddedInFountain(): boolean {
  return getIsEmbeddedInFountain();
}
